package br.com.heyagenda.domain.model

import kotlinx.serialization.Serializable

@Serializable
enum class DayOfWeekName(val label: String, val shortLabel: String) {
    MONDAY("Segunda-feira", "Seg"),
    TUESDAY("Terça-feira", "Ter"),
    WEDNESDAY("Quarta-feira", "Qua"),
    THURSDAY("Quinta-feira", "Qui"),
    FRIDAY("Sexta-feira", "Sex"),
    SATURDAY("Sábado", "Sáb"),
    SUNDAY("Domingo", "Dom")
}

@Serializable
data class TimeInterval(
    val startTime: String = "08:00",
    val endTime: String = "12:00"
)

@Serializable
data class DaySchedule(
    val dayOfWeek: DayOfWeekName,
    val isOpen: Boolean = true,
    val startTime: String = "08:00",
    val endTime: String = "18:00",
    val breakStart: String? = null,
    val breakEnd: String? = null,
    val intervals: List<TimeInterval> = emptyList()
) {
    val effectiveIntervals: List<TimeInterval>
        get() {
            if (!isOpen) return emptyList()
            if (intervals.isNotEmpty()) {
                val valid = intervals.filter { it.startTime.isNotBlank() && it.endTime.isNotBlank() && it.startTime < it.endTime }
                if (valid.isNotEmpty()) return valid
            }
            val bStart = breakStart?.takeIf { it.isNotBlank() }
            val bEnd = breakEnd?.takeIf { it.isNotBlank() }
            if (bStart != null && bEnd != null && bStart > startTime && bEnd < endTime) {
                return listOf(
                    TimeInterval(startTime, bStart),
                    TimeInterval(bEnd, endTime)
                )
            }
            if (startTime.isNotBlank() && endTime.isNotBlank() && startTime < endTime) {
                return listOf(TimeInterval(startTime, endTime))
            }
            return emptyList()
        }

    val formattedIntervals: String
        get() {
            if (!isOpen) return "Fechado"
            val list = effectiveIntervals
            if (list.isEmpty()) return "Fechado"
            return list.joinToString(" e ") { "${it.startTime} às ${it.endTime}" }
        }
}

@Serializable
data class DayOff(
    val id: String,
    val dateIso: String, // format YYYY-MM-DD e.g. "2026-12-25"
    val reason: String, // e.g. "Feriado de Natal", "Folga", "Férias"
    val isRecurringYearly: Boolean = false
)

@Serializable
data class DateScheduleOverride(
    val id: String,
    val dateIso: String, // format YYYY-MM-DD e.g. "2026-09-19"
    val isOpen: Boolean = true,
    val startTime: String = "08:00",
    val endTime: String = "18:00",
    val breakStart: String? = null,
    val breakEnd: String? = null,
    val reason: String? = null, // e.g. "Horário especial", "Saída antecipada", "Plantão"
    val intervals: List<TimeInterval> = emptyList()
) {
    val effectiveIntervals: List<TimeInterval>
        get() {
            if (!isOpen) return emptyList()
            if (intervals.isNotEmpty()) {
                val valid = intervals.filter { it.startTime.isNotBlank() && it.endTime.isNotBlank() && it.startTime < it.endTime }
                if (valid.isNotEmpty()) return valid
            }
            val bStart = breakStart?.takeIf { it.isNotBlank() }
            val bEnd = breakEnd?.takeIf { it.isNotBlank() }
            if (bStart != null && bEnd != null && bStart > startTime && bEnd < endTime) {
                return listOf(
                    TimeInterval(startTime, bStart),
                    TimeInterval(bEnd, endTime)
                )
            }
            if (startTime.isNotBlank() && endTime.isNotBlank() && startTime < endTime) {
                return listOf(TimeInterval(startTime, endTime))
            }
            return emptyList()
        }

    val formattedIntervals: String
        get() {
            if (!isOpen) return "Fechado"
            val list = effectiveIntervals
            if (list.isEmpty()) return "Fechado"
            return list.joinToString(" e ") { "${it.startTime} às ${it.endTime}" }
        }
}

@Serializable
data class ProfessionalAvailability(
    val weeklySchedule: List<DaySchedule> = listOf(
        DaySchedule(DayOfWeekName.MONDAY, isOpen = true, startTime = "08:00", endTime = "18:00"),
        DaySchedule(DayOfWeekName.TUESDAY, isOpen = true, startTime = "08:00", endTime = "18:00"),
        DaySchedule(DayOfWeekName.WEDNESDAY, isOpen = true, startTime = "08:00", endTime = "18:00"),
        DaySchedule(DayOfWeekName.THURSDAY, isOpen = true, startTime = "08:00", endTime = "18:00"),
        DaySchedule(DayOfWeekName.FRIDAY, isOpen = true, startTime = "08:00", endTime = "18:00"),
        DaySchedule(DayOfWeekName.SATURDAY, isOpen = true, startTime = "08:00", endTime = "14:00", breakStart = null, breakEnd = null),
        DaySchedule(DayOfWeekName.SUNDAY, isOpen = false, startTime = "08:00", endTime = "12:00", breakStart = null, breakEnd = null)
    ),
    val daysOff: List<DayOff> = listOf(
        DayOff(id = "d1", dateIso = "2026-12-25", reason = "Feriado de Natal", isRecurringYearly = true),
        DayOff(id = "d2", dateIso = "2026-01-01", reason = "Ano Novo", isRecurringYearly = true)
    ),
    val dateOverrides: List<DateScheduleOverride> = emptyList()
)
